import React, { useEffect, useMemo, useState } from "react";
import { formatSuggestionStock, parseSuggestionStock } from "./session-builder-stock.js";

const initial = {
  music_genres: ["ambient"], session_type: "hypnosis", setting: "beach",
  focuses: ["confidence"], voice_genders: ["neutral"], duration_hours: 1,
  nsfw: false, adult_focuses: [], adult_styles: [], written_materials: [], listener_gender: "prefer_not_to_say",
  preferences: ["unspecified"], adult_suggestions: "", template_id: "", bundle_id: "",
  evening_start_time: "20:00", craving_substitutions: [], custom_substitution: "", strong_induction: false,
};

function Checks({ title, items = [], values, onChange, maximum = 99 }) {
  const groupId = String(title || "options").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  return <fieldset className="session-builder-group"><legend>{title}</legend><div className="session-builder-checks">
    {items.map((item) => {
      const fieldId = `session-${groupId}-${String(item.id).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      return <label key={item.id} htmlFor={fieldId}><input id={fieldId} name={`${groupId}[]`} type="checkbox" checked={values.includes(item.id)} onChange={() => {
        const next = values.includes(item.id) ? values.filter((x) => x !== item.id) : values.length < maximum ? [...values, item.id] : values;
        onChange(next);
      }} /> <span>{item.label}</span>{item.adult_only ? <small>18+</small> : null}</label>;
    })}
  </div></fieldset>;
}

export function SessionBuilder({ options, open, busy, error, onClose, onCompile, initialForm, onChange, initialMode = "prebuilt" }) {
  const [form, setForm] = useState(() => ({
    ...initial,
    ...(initialForm || {}),
    adult_suggestions: formatSuggestionStock(initialForm?.adult_suggestions),
  }));
  useEffect(() => {
    if (initialForm && typeof initialForm === "object") {
      setForm({
        ...initial,
        ...initialForm,
        adult_suggestions: formatSuggestionStock(initialForm.adult_suggestions),
      });
    }
  }, [initialForm]);
  useEffect(() => {
    if (open && typeof onChange === "function") onChange(form);
  }, [form, onChange, open]);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  // Pre-built sessions and the custom builder are two different jobs, so they
  // get two tabs instead of one long stacked form.  Pre-built opens first: it
  // is the fastest path into a session.
  const [mode, setMode] = useState(initialMode === "build" ? "build" : "prebuilt");
  const switchMode = (next) => {
    setMode(next);
    // A bundle selection only means anything on the pre-built tab. Leaving it
    // set would let the build form silently submit the bundle instead.
    if (next === "build") {
      setForm((current) => (current.bundle_id ? { ...current, bundle_id: "" } : current));
    }
  };
  const setSetting = (value) => setForm((current) => {
    const selected = (options?.session_templates || []).find((item) => item.id === current.template_id);
    const settingChanged = selected && String(selected.request_defaults?.setting || "") !== String(value || "");
    return { ...current, setting: value, ...(settingChanged ? { template_id: "" } : {}) };
  });
  const applyTemplate = (templateId) => {
    const template = (options.session_templates || []).find((item) => item.id === templateId);
    if (!template) {
      set("template_id", "");
      return;
    }
    setForm((current) => ({
      ...current,
      ...(template.request_defaults || {}),
      template_id: template.id,
      adult_suggestions: formatSuggestionStock(template.request_defaults?.adult_suggestions),
    }));
  };
  const focusCount = form.focuses.length + form.adult_focuses.length;
  const maxGeneral = Math.max(0, 3 - form.adult_focuses.length);
  const maxAdult = Math.max(0, 3 - form.focuses.length);
  const suggestionStock = useMemo(() => parseSuggestionStock(form.adult_suggestions), [form.adult_suggestions]);
  const selectedTemplate = useMemo(
    () => (options?.session_templates || []).find((item) => item.id === form.template_id),
    [options, form.template_id],
  );
  const cravingSelected = form.focuses.includes("evening_craving_redirection")
    || form.adult_focuses.includes("sensual_craving_redirection")
    || form.focuses.includes("habit_change")
    || form.adult_focuses.includes("sensual_habit_redirection");
  const canSubmit = useMemo(() => {
    // A pre-built bundle is self-contained: it carries its own voice, themes,
    // and programs, so it does not need the generic focus/voice requirements.
    if (mode === "prebuilt") return Boolean(form.bundle_id);
    return form.music_genres.length && focusCount > 0 && focusCount <= 3 && form.voice_genders.length;
  }, [form, focusCount, mode]);
  if (!open || !options) return null;
  return <div className="session-builder-backdrop" role="dialog" aria-modal="true" aria-labelledby="new-session-title">
    <form className="session-builder" onSubmit={(event) => {
      event.preventDefault();
      if (canSubmit) onCompile({ ...form, adult_suggestions: formatSuggestionStock(form.adult_suggestions) });
    }}>
      <header><div><p className="eyebrow">HypnoMates</p><h2 id="new-session-title">Load a session or create one</h2><p>Start from a fully-authored pre-built session, or build your own from scratch.</p></div>{onClose ? <button type="button" onClick={onClose} aria-label="Close">×</button> : null}</header>
      <div className="session-builder-tabs" role="tablist" aria-label="Session source">
        <button
          type="button"
          role="tab"
          id="session-tab-prebuilt"
          aria-controls="session-panel-prebuilt"
          aria-selected={mode === "prebuilt"}
          className={mode === "prebuilt" ? "active" : ""}
          onClick={() => switchMode("prebuilt")}
        >
          ⬇ Pre-built sessions
          <small>Load one instantly</small>
        </button>
        <button
          type="button"
          role="tab"
          id="session-tab-build"
          aria-controls="session-panel-build"
          aria-selected={mode === "build"}
          className={mode === "build" ? "active" : ""}
          onClick={() => switchMode("build")}
        >
          ✎ Build my own
          <small>Choose every detail</small>
        </button>
      </div>
      <div className="session-builder-grid">
        {mode === "prebuilt" ? (
        <fieldset
          className="session-builder-group session-builder-bundles"
          id="session-panel-prebuilt"
          role="tabpanel"
          aria-labelledby="session-tab-prebuilt"
        ><legend>⬇ Pre-built sessions — load one instantly</legend>
          <p className="session-builder-help">Fully authored, validated sessions with their own voice, music, and deepening arc. Pick one and press <strong>Load session</strong> — everything is ready to play.</p>
          <div className="session-builder-bundle-grid">
            {(options.bundles || []).map((bundle) => {
              const selected = form.bundle_id === bundle.bundle_id;
              return (
                <div key={bundle.bundle_id} className={`session-builder-bundle-card${selected ? " selected" : ""}${bundle.mode === "nsfw" ? " nsfw" : ""}`}>
                  <label className="session-builder-bundle-main">
                    <input type="radio" name="bundle_id" value={bundle.bundle_id} checked={selected} onChange={() => set("bundle_id", bundle.bundle_id)} />
                    <span className="session-builder-bundle-title">{bundle.title}</span>
                    <span className="session-builder-bundle-meta">{bundle.mode === "nsfw" ? "18+" : "SFW"} · {bundle.voice_gender} voice · {bundle.location}</span>
                    <span className="session-builder-bundle-desc">{bundle.description}</span>
                    {bundle.themes?.length ? <span className="session-builder-bundle-themes"><strong>Themes:</strong> {bundle.themes.join(", ")}</span> : null}
                    {bundle.suggestions?.length ? <span className="session-builder-bundle-lane"><strong>Suggestions:</strong> {bundle.suggestions[0]}</span> : null}
                    {bundle.commands?.length ? <span className="session-builder-bundle-lane"><strong>Commands:</strong> {bundle.commands[0]}</span> : null}
                    {bundle.post_hypnotic_suggestions?.length ? <span className="session-builder-bundle-lane"><strong>Post-hypnotic:</strong> {bundle.post_hypnotic_suggestions[0]}</span> : null}
                    {bundle.triggers?.length ? <span className="session-builder-bundle-lane"><strong>Triggers:</strong> {bundle.triggers.join(" · ")}</span> : null}
                    {bundle.reward_escalation?.length ? <span className="session-builder-bundle-lane session-builder-bundle-rewards"><strong>Reward escalation:</strong> {bundle.reward_escalation[0]} <em>→ {bundle.reward_escalation[bundle.reward_escalation.length - 1]}</em></span> : null}
                  </label>
                  <button
                    type="button"
                    className="session-builder-bundle-load"
                    disabled={busy}
                    onClick={() => { set("bundle_id", bundle.bundle_id); onCompile({ ...form, bundle_id: bundle.bundle_id, adult_suggestions: formatSuggestionStock(form.adult_suggestions) }); }}
                  >
                    {busy ? "Compiling…" : selected ? "✓ Load this session" : "Load session"}
                  </button>
                </div>
              );
            })}
          </div>
        </fieldset>
        ) : (
        <div
          className="session-builder-build-panel"
          id="session-panel-build"
          role="tabpanel"
          aria-labelledby="session-tab-build"
        >
        <Checks title="Music — choose several" items={options.music_genres} values={form.music_genres} onChange={(v) => set("music_genres", v)} />
        <fieldset className="session-builder-group session-builder-template"><legend>Curated session</legend>
          <label htmlFor="session-template">Start from a pre-written session
            <select id="session-template" name="template_id" value={form.template_id || ""} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">Build from my choices</option>
              {(options.session_templates || []).map((template) => <option key={template.id} value={template.id}>{template.label}{template.adult_only ? " · 18+" : ""}</option>)}
            </select>
          </label>
          {selectedTemplate ? <>
            <p className="session-builder-template-description">{selectedTemplate.description}</p>
            <p className="session-builder-help">The authored blueprint fills the choices below. You can still adjust them before building; the normal session validation and paragraph plan remain in control.</p>
            <small className="session-builder-template-arc">{(selectedTemplate.composition?.arc || []).map((item) => item.phase).join(" → ")}</small>
            <div className="session-builder-template-contract" aria-label="Curated session contract">
              <span><strong>Scene:</strong> {selectedTemplate.scene_contract?.place || selectedTemplate.request_defaults?.setting}</span>
              <span><strong>Voice:</strong> {selectedTemplate.voice_contract?.pace || "slow and spacious"}</span>
              {selectedTemplate.adult_only ? <span><strong>Overlay root:</strong> {(selectedTemplate.scene_contract?.anchors || []).slice(0, 2).join(" · ")}</span> : null}
            </div>
          </> : <p className="session-builder-help">Choose a proven starting shape, or keep building a custom session.</p>}
        </fieldset>
        <fieldset className="session-builder-group"><legend>Session structure</legend>
          <label htmlFor="session-type">Type<select id="session-type" name="session_type" value={form.session_type} onChange={(e) => set("session_type", e.target.value)}>{(options.session_types || []).map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</select></label>
          <label htmlFor="session-setting">Setting<select id="session-setting" name="setting" value={form.setting} onChange={(e) => setSetting(e.target.value)}>{(options.settings || []).map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</select></label>
          <label htmlFor="session-duration">Length <strong>{form.duration_hours} hour{Number(form.duration_hours) === 1 ? "" : "s"}</strong><input id="session-duration" name="duration_hours" type="range" min="0.5" max="8" step="0.5" value={form.duration_hours} onChange={(e) => set("duration_hours", Number(e.target.value))} /></label>
          <label className="session-builder-switch" htmlFor="session-strong-induction"><input id="session-strong-induction" name="strong_induction" type="checkbox" checked={Boolean(form.strong_induction)} onChange={(e) => set("strong_induction", e.target.checked)} /> Strong Induction</label>
          <p className="session-builder-help">Longer, voice-forward induction with progressive relaxation, ordered counting, fractionation, sensory narrowing, and continued deepening before later suggestions.</p>
        </fieldset>
        <Checks title={`Session focus — ${focusCount}/3`} items={options.focuses} values={form.focuses} maximum={maxGeneral} onChange={(v) => set("focuses", v)} />
        {cravingSelected && <fieldset className="session-builder-group session-builder-craving"><legend>Evening craving replacement</legend>
          <p>Rehearse one repeatable response for the exact time and craving cue.</p>
          <label htmlFor="session-evening-start">Evening begins<input id="session-evening-start" name="evening_start_time" type="time" value={form.evening_start_time} onChange={(e) => set("evening_start_time", e.target.value)} /></label>
          <Checks
            title="Redirect the urge into"
            items={[...(options.craving_substitutions || []), ...(form.nsfw ? options.adult_craving_substitutions || [] : [])]}
            values={form.craving_substitutions}
            onChange={(v) => set("craving_substitutions", v)}
          />
          <label htmlFor="session-custom-substitution">Custom replacement<textarea id="session-custom-substitution" name="custom_substitution" maxLength="240" value={form.custom_substitution} onChange={(e) => set("custom_substitution", e.target.value)} placeholder="Optional: the specific evening ritual or partner connection you want rehearsed…" /><small>{form.custom_substitution.length}/240</small></label>
        </fieldset>}
        <Checks
          title="Voice"
          items={options.voice_genders}
          values={form.voice_genders}
          // A voice choice is singular. The initial neutral checkbox is only a
          // no-preference default; it must not remain alongside female/male and
          // make the request look like a multi-voice cast.
          onChange={(v) => {
            const explicit = v.find((voice) => voice !== "neutral");
            set("voice_genders", explicit ? [explicit] : v.includes("neutral") ? ["neutral"] : []);
          }}
        />
        <Checks title="Optional written material" items={options.written_materials.filter((x) => form.nsfw || !x.adult_only)} values={form.written_materials} onChange={(v) => set("written_materials", v)} />
        <fieldset className="session-builder-group session-builder-adult"><legend>Adult session</legend>
          <label className="session-builder-switch" htmlFor="session-nsfw"><input id="session-nsfw" name="nsfw" type="checkbox" checked={form.nsfw} onChange={(e) => setForm((f) => {
            const nextNsfw = e.target.checked;
            const selected = (options.session_templates || []).find((item) => item.id === f.template_id);
            const adultTemplateDisabled = selected?.adult_only && !nextNsfw;
            return { ...f, nsfw: nextNsfw, ...(adultTemplateDisabled ? { template_id: "" } : {}), adult_focuses: nextNsfw ? f.adult_focuses : [], adult_styles: nextNsfw ? f.adult_styles : [], adult_suggestions: nextNsfw ? f.adult_suggestions : "", craving_substitutions: nextNsfw ? f.craving_substitutions : f.craving_substitutions.filter((x) => !(options.adult_craving_substitutions || []).some((item) => item.id === x)), written_materials: nextNsfw ? f.written_materials : f.written_materials.filter((x) => !["erotic_readings", "kama_sutra_inspired"].includes(x)) };
          })} /> NSFW / 18+ customization</label>
          <p>{options.notices?.adult}</p>
          {form.nsfw && <>
            <Checks title={`Adult focus — ${focusCount}/3 total`} items={options.adult_focuses} values={form.adult_focuses} maximum={maxAdult} onChange={(v) => set("adult_focuses", v)} />
            <Checks title="Adult style" items={options.adult_styles || []} values={form.adult_styles} onChange={(v) => set("adult_styles", v)} />
            <label htmlFor="session-listener-gender">Your gender<select id="session-listener-gender" name="listener_gender" value={form.listener_gender} onChange={(e) => set("listener_gender", e.target.value)}>{(options.listener_genders || []).map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}</select></label>
            <Checks title="Attraction / preference" items={options.preferences} values={form.preferences} onChange={(v) => set("preferences", v)} />
            <label htmlFor="session-adult-suggestions">Adult-only suggestions (semicolon-separated)<textarea id="session-adult-suggestions" name="adult_suggestions" maxLength="600" value={form.adult_suggestions} onChange={(e) => set("adult_suggestions", e.target.value)} placeholder="Goal or theme one; another goal or theme; optional Inspired-By direction…" /><small>{form.adult_suggestions.length}/600 · {suggestionStock.length} stock item{suggestionStock.length === 1 ? "" : "s"}</small></label>
            <p className="session-builder-help">Each item is suggestion stock for this session. Generated suggestions will target the selected goals/themes, and may be rewritten through the Inspired-By system.</p>
          </>}
        </fieldset>
        </div>
        )}
      </div>
      <footer><div>{error ? <strong className="session-builder-error">{error}</strong> : <span>{options.notices?.effects}</span>}</div><button className="primary" disabled={!canSubmit || busy}>{busy ? "Compiling coherent queue…" : mode === "prebuilt" ? "Load this session" : "Build my session"}</button></footer>
    </form>
  </div>;
}
